/**
 * Trailblazers (TRL) sabotage types.
 *
 * Before the game starts, each team's captain may secretly place ONE sabotage on
 * ONE planet of the galaxy map (never a starting planet, never the final one).
 * The sabotage lasts the whole game and affects every OTHER team that comes to
 * that planet; the placing team is never affected by its own sabotage.
 *
 * Not to be confused with Disruption (`trlDisruption.ts`), the mid-cast Sparkle
 * Powder scanner block — that is a separate mechanic.
 *
 * This module is the single registry: every sabotage type is declared once in
 * {@link TRL_SABOTAGE_TYPES} / {@link TRL_SABOTAGE_DEFINITIONS}, and the admin
 * settings, the player placement UI and the server hooks all iterate it. Adding
 * a type means an entry here, its config slice, and one server module.
 *
 * Config lives game-level on `games.trl_sabotage_config`; placements persist in
 * `trl_sabotage_placements` and triggered effects (thefts, burned-out
 * fake clues, intercepted finds) in `trl_sabotage_effects`.
 */
// ============================================================================
// Registry
// ============================================================================
export const TRL_SABOTAGE_TYPES = ['treasure_thief', 'con_clue', 'scanner_spy'];
export const TRL_SABOTAGE_DEFINITIONS = {
    treasure_thief: {
        label: 'Treasure Thief',
        description: 'Every rival team that clears this planet has part of its treasure haul stolen and handed to your team.',
        emoji: '🦹',
        requiresDecoyImage: false,
        requiresTriangulationNode: false,
    },
    con_clue: {
        label: 'Con Clue',
        description: 'Rival teams are shown a fake screenshot for this planet. They must find it before learning it was a con and receiving the real one.',
        emoji: '🎭',
        requiresDecoyImage: true,
        requiresTriangulationNode: false,
    },
    scanner_spy: {
        label: 'Scanner Spy',
        description: 'Triangulation planets only. The first screenshot each rival team finds here is handed to your team — marked found, with its coordinates unlocked. The final target is never revealed.',
        emoji: '🛰️',
        requiresDecoyImage: false,
        requiresTriangulationNode: true,
    },
};
export function isTrlSabotageType(raw) {
    return TRL_SABOTAGE_TYPES.includes(raw);
}
// ============================================================================
// Config (admin-authored, game-level only)
// ============================================================================
/** Bounds on Treasure Thief's steal ratio, in whole percent of the victim's haul. */
export const TRL_THEFT_MIN_PERCENT = 5;
export const TRL_THEFT_MAX_PERCENT = 100;
/** Defaults: every type off, so existing games are unaffected. */
export const DEFAULT_TRL_SABOTAGE_CONFIG = {
    types: {
        treasure_thief: { enabled: false, stealPercent: 25 },
        con_clue: { enabled: false },
        scanner_spy: { enabled: false },
    },
};
/** Effective config for a game: authored config, else built-in defaults. */
export function resolveTrlSabotageConfig(gameConfig) {
    return gameConfig ?? DEFAULT_TRL_SABOTAGE_CONFIG;
}
export function isTrlSabotageTypeEnabled(gameConfig, type) {
    return resolveTrlSabotageConfig(gameConfig).types[type].enabled;
}
/** The enabled types, in registry order. */
export function enabledTrlSabotageTypes(gameConfig) {
    return TRL_SABOTAGE_TYPES.filter((type) => isTrlSabotageTypeEnabled(gameConfig, type));
}
/** Value-equality across every persisted field. */
export function trlSabotageConfigEquals(a, b) {
    return JSON.stringify(normalizeTrlSabotageConfig(a)) === JSON.stringify(normalizeTrlSabotageConfig(b));
}
/** Sanitise untrusted admin input — normalise per field, never reject. */
export function normalizeTrlSabotageConfig(raw) {
    const d = DEFAULT_TRL_SABOTAGE_CONFIG.types;
    const types = raw?.types ?? {};
    const thief = (types.treasure_thief ?? {});
    const conClue = (types.con_clue ?? {});
    const scannerSpy = (types.scanner_spy ?? {});
    const pctRaw = Number(thief.stealPercent);
    const stealPercent = Number.isFinite(pctRaw)
        ? Math.min(TRL_THEFT_MAX_PERCENT, Math.max(TRL_THEFT_MIN_PERCENT, Math.round(pctRaw)))
        : d.treasure_thief.stealPercent;
    return {
        types: {
            treasure_thief: { enabled: Boolean(thief.enabled), stealPercent },
            con_clue: { enabled: Boolean(conClue.enabled) },
            scanner_spy: { enabled: Boolean(scannerSpy.enabled) },
        },
    };
}
// ============================================================================
// Placement eligibility
// ============================================================================
/**
 * A sabotage may sit on any planet except a starting planet or the final one.
 *
 * This is the type-AGNOSTIC rule — it answers "could any sabotage go here", which
 * is what the lobby fog and the map validator's per-planet checks need. A given
 * type may narrow it further; use {@link isTrlSabotageEligibleNodeForType} once a
 * type has been chosen.
 */
export function isTrlSabotageEligibleNode(node) {
    return !node.isStart && !node.isFinal;
}
/**
 * Whether a SPECIFIC sabotage type may be placed on this planet: the shared rule
 * above, plus the type's own requirements. Scanner Spy acts on a planet's
 * individual screenshots, so it needs a triangulation planet.
 */
export function isTrlSabotageEligibleNodeForType(node, type) {
    if (!isTrlSabotageEligibleNode(node))
        return false;
    if (TRL_SABOTAGE_DEFINITIONS[type].requiresTriangulationNode && node.roundType !== 'triangulation') {
        return false;
    }
    return true;
}
// ============================================================================
// Treasure hauls + theft
// ============================================================================
/**
 * Replay awards then thefts (in time order) into each team's current haul.
 * A theft entry naming an instance the victim no longer holds is skipped, so a
 * stale or duplicated record can never duplicate an item.
 *
 * The instance key includes the TEAM because a node can carry more than one award row: the
 * winner's placed treasure plus a consolation roll for each runner-up. Keying on nodeId alone
 * would make those collide, so a consolation item would be swallowed by the `byInstance`
 * guard or silently overwrite the winner's.
 */
export function computeTrlTeamHauls(awards, effects) {
    const owner = new Map();
    const byInstance = new Map();
    const order = [];
    for (const award of awards ?? []) {
        award.items.forEach((item, index) => {
            const instanceId = `${award.nodeId}:${award.teamId}:${index}`;
            if (byInstance.has(instanceId))
                return;
            byInstance.set(instanceId, { instanceId, item });
            owner.set(instanceId, award.teamId);
            order.push(instanceId);
        });
    }
    const thefts = (effects ?? [])
        .filter((e) => e.payload.kind === 'treasure_thief')
        .slice()
        .sort((a, b) => a.createdAt - b.createdAt);
    for (const theft of thefts) {
        if (theft.payload.kind !== 'treasure_thief' || !theft.saboteurTeamId)
            continue;
        for (const held of theft.payload.stolen) {
            if (owner.get(held.instanceId) !== theft.victimTeamId)
                continue;
            owner.set(held.instanceId, theft.saboteurTeamId);
        }
    }
    const hauls = new Map();
    for (const instanceId of order) {
        const teamId = owner.get(instanceId);
        const list = hauls.get(teamId) ?? [];
        list.push(byInstance.get(instanceId));
        hauls.set(teamId, list);
    }
    return hauls;
}
/**
 * Relative odds of an item being picked by Treasure Thief. Smaller (lower-tier)
 * rewards are the likely target, but a high-tier item can still go.
 */
export const TRL_THEFT_TIER_WEIGHTS = {
    low: 6,
    mid: 3,
    high: 1,
};
/** How many items a theft takes from a haul of `heldCount`: ceil(pct), at least 1. */
export function trlTheftCount(heldCount, stealPercent) {
    if (heldCount <= 0)
        return 0;
    return Math.min(heldCount, Math.max(1, Math.ceil((heldCount * stealPercent) / 100)));
}
/**
 * Pick the items a Treasure Thief takes: {@link trlTheftCount} items by
 * tier-weighted random sampling without replacement. `rng` returns [0, 1).
 */
export function selectTrlTheftItems(haul, stealPercent, rng = Math.random) {
    const remaining = [...haul];
    const picked = [];
    const count = trlTheftCount(remaining.length, stealPercent);
    while (picked.length < count && remaining.length > 0) {
        const total = remaining.reduce((sum, h) => sum + TRL_THEFT_TIER_WEIGHTS[h.item.tier], 0);
        let roll = rng() * total;
        let index = remaining.length - 1;
        for (let i = 0; i < remaining.length; i++) {
            roll -= TRL_THEFT_TIER_WEIGHTS[remaining[i].item.tier];
            if (roll < 0) {
                index = i;
                break;
            }
        }
        picked.push(remaining.splice(index, 1)[0]);
    }
    return picked;
}
// ============================================================================
// Con Clue
// ============================================================================
/**
 * Which of a planet's images a Con Clue replaces for a given team. Stable (a
 * string hash), so the server's dig path and the per-viewer snapshot mask always
 * agree without persisting anything; it varies between teams.
 */
export function pickTrlConClueSlotIndex(nodeId, teamId, imageCount) {
    if (imageCount <= 1)
        return 0;
    const seed = `${nodeId}:${teamId}`;
    let hash = 2166136261;
    for (let i = 0; i < seed.length; i++) {
        hash ^= seed.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0) % imageCount;
}
/**
 * True while a Con Clue fools `teamId` on `nodeId`: another team placed one there
 * and this team has not yet burned it out.
 */
export function resolveTrlActiveConClue(placements, effects, nodeId, teamId) {
    if (!teamId)
        return false;
    const placed = (placements ?? []).some((p) => p.type === 'con_clue' && p.nodeId === nodeId && p.teamId !== teamId);
    if (!placed)
        return false;
    return !(effects ?? []).some((e) => e.type === 'con_clue' && e.nodeId === nodeId && e.victimTeamId === teamId);
}
/**
 * The image a conned team sees in the replaced slot: the fake's public metadata,
 * keeping the real slot's identity (`id`, `sortOrder`) so found-tracking still
 * works, and its difficulty so the route-planning badge never changes.
 */
export function applyTrlDecoyToImageRef(real, decoy) {
    return {
        ...real,
        imageId: decoy.imageId,
        imageUrl: decoy.imageUrl,
        thumbnailUrl: decoy.thumbnailUrl,
        imageName: decoy.imageName,
        imageArea: decoy.imageArea,
        logAreaId: decoy.logAreaId,
        faction: decoy.faction,
    };
}
// ============================================================================
// Scanner Spy
// ============================================================================
/**
 * The node-image ids each team has been handed by its Scanner Spy, keyed by the
 * SABOTEUR's team id. The grant itself lives in the team's `found_image_ids`, but
 * a spied screenshot must also surrender its coordinates, and the snapshot only
 * reveals found-image coordinates under simultaneous broadcast — so the progress
 * read needs to know which ids were spied.
 *
 * Server-side only in practice: `maskTrlSabotageEffects` gives a client just the
 * effects it caused or suffered, which is exactly its own grants, but the
 * coordinates are resolved before masking.
 */
export function collectTrlScannerSpyGrants(effects) {
    const byTeam = new Map();
    for (const effect of effects ?? []) {
        if (effect.payload.kind !== 'scanner_spy' || !effect.saboteurTeamId)
            continue;
        const ids = byTeam.get(effect.saboteurTeamId) ?? new Set();
        ids.add(effect.payload.nodeImageId);
        byTeam.set(effect.saboteurTeamId, ids);
    }
    return byTeam;
}
